import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface LoadingScreenProps {
  onComplete?: () => void
}

export default function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const [progress, setProgress] = useState(0)
  const [visible, setVisible] = useState(true)
  const [status, setStatus] = useState<'loading' | 'ready'>('loading')

  useEffect(() => {
    const steps = [3, 7, 12, 18, 26, 35, 45, 55, 63, 70, 76, 82, 87, 91, 94, 97, 100]
    let idx = 0
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const timer = setInterval(() => {
      if (idx < steps.length) {
        setProgress(steps[idx])
        idx++
      } else {
        clearInterval(timer)
        setStatus('ready')
        timeoutId = setTimeout(() => {
          setVisible(false)
          onComplete?.()
        }, 800)
      }
    }, 120)
    return () => {
      clearInterval(timer)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center"
          style={{ background: '#050B14' }}
        >
          {/* 外环装饰 */}
          <motion.div
            className="w-32 h-32 rounded-full border border-sci-cyan/10 mb-8 flex items-center justify-center"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          >
            <div className="w-28 h-28 rounded-full border border-sci-cyan/20" />
          </motion.div>

          <h1
            className="text-2xl sm:text-3xl font-bold text-sci-white sci-text-glow tracking-[0.25em] mb-6"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            SOLAR SYSTEM
          </h1>

          {/* 进度条外框 */}
          <div
            className="w-56 sm:w-64 h-1 bg-space-700 rounded-full overflow-hidden mb-3"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="页面加载进度"
          >
            <motion.div
              className="h-full rounded-full"
              style={{ background: '#4ECDC4' }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.25 }}
            />
          </div>

          <motion.p
            className="text-xs text-sci-cyan/60 font-mono tracking-wider"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            {status === 'loading' ? 'LOADING SYSTEM... ' + progress + '%' : 'SYSTEM READY'}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
